/**
 * Product analytics behind one small API so components never talk to the vendor directly.
 * Plausible (EU, cookieless, no personal data). Its manual script sends only the page views we
 * hand it: on load and on every path change (like its automatic script, which would send the
 * full URL), with the URL cleaned by `pageUrl()`. Nothing loads and nothing is sent without
 * VITE_PLAUSIBLE_DOMAIN (dev, CI, e2e).
 *
 *   track("invoice_created", { kind: "invoice" })   custom events (a goal in Plausible each)
 */
import { env } from "@/lib/core/env";

export type EventProps = Record<string, string | number | boolean | null | undefined>;

type Plausible = ((event: string, options?: { props?: EventProps; u?: string }) => void) & {
  q?: unknown[];
};

declare global {
  interface Window {
    plausible?: Plausible;
  }
}

export const PLAUSIBLE_SCRIPT = "https://plausible.io/js/script.manual.js";

/** A PocketBase record id: 15 lowercase letters and digits. */
const RECORD_ID = /^[a-z0-9]{15}$/;

/**
 * The page as Plausible sees it: record ids in the path become ":id" (/invoices/:id) and the
 * query goes (list search text, customer filters), except the campaign tags (`utm_*`).
 */
export function pageUrl(location: Pick<Location, "origin" | "pathname" | "search">): string {
  const path = location.pathname
    .split("/")
    .map((part) => (RECORD_ID.test(part) ? ":id" : part))
    .join("/");
  const params = [...new URLSearchParams(location.search)].filter(([key]) =>
    key.startsWith("utm_"),
  );
  const query = new URLSearchParams(params).toString();
  return `${location.origin}${path}${query ? `?${query}` : ""}`;
}

let lastPath: string | null = null;

/** One page view per path, as Plausible's own script counts them. */
function pageview() {
  if (window.location.pathname === lastPath) return;
  lastPath = window.location.pathname;
  window.plausible?.("pageview", { u: pageUrl(window.location) });
}

/** Call once at startup (main.tsx): adds Plausible's script and a queue for early events. */
export function initAnalytics(): void {
  if (!env.plausibleDomain || document.querySelector(`script[src="${PLAUSIBLE_SCRIPT}"]`)) return;
  window.plausible ??= function (...args: unknown[]) {
    (window.plausible!.q ??= []).push(args);
  };
  const script = document.createElement("script");
  script.defer = true;
  script.dataset.domain = env.plausibleDomain;
  script.src = PLAUSIBLE_SCRIPT;
  document.head.appendChild(script);

  // React Router navigates with pushState (Back and Forward fire popstate).
  const push = history.pushState;
  history.pushState = function (...args: Parameters<History["pushState"]>) {
    push.apply(this, args);
    pageview();
  };
  window.addEventListener("popstate", pageview);
  lastPath = null;
  pageview();
}

/** Props must never carry personal data (names, emails, ids): counts, kinds and flags only. */
export function track(event: string, props?: EventProps): void {
  if (!env.plausibleDomain) return;
  window.plausible?.(event, props ? { props } : undefined);
}
